import { useState, useEffect, useRef } from 'react';
import { ResellerSiderItem } from 'src/enums/IResellerSiderItems';
import ResellerDashboard from '../ResellerDashboard';
import {
	Button,
	Card,
	ColorPicker,
	Form,
	GetProp,
	Input,
	notification,
	Switch,
	Upload,
	UploadProps,
	Slider,
	Typography,
	Tag,
	Tooltip,
} from 'antd';
import {
	LoadingOutlined,
	PlusOutlined,
	BgColorsOutlined,
	InfoCircleOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
	UPDATE_RESELLER_LOGO,
	UpdateResellerBranding,
	UpdateResellerBrandingToggle,
	updateTutorialStepStatus,
	updateTourStatus,
} from '@/api/reseller';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getResellerData } from '@/store/reseller/selector';
import { updateReseller } from '@/store/reseller/slice';
import { CustomModal } from '@/components/CustomModal';
import './ResellerBranding.scss';
import ThemeCustomizer from './Modals/ThemeCustomizeModal/ThemeCustomizer';
import { openModal } from '@/store/modal/slice';
import { MODAL_IDS } from 'src/enums/EModal';
import { BookOpenIcon, PaletteIcon } from 'lucide-react';
import TutorialsModal from '../TutorialsModal';
import TourButton from '@/components/TourButton/TourButton';
import ResellerTour from '../ResellerTour/ResellerTour';
import { setTour } from '@/store/resellerTour/slice';
import { AppRoutes } from 'src/enums/ERoutes';
import { getResellerTourState } from '@/store/resellerTour/selector';

const { Title, Text } = Typography;

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const getBase64 = (img: FileType, callback: (url: string) => void) => {
	const reader = new FileReader();
	reader.addEventListener('load', () => callback(reader.result as string));
	reader.readAsDataURL(img);
};

const tutorialItems = [
	{
		icon: (
			<div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-full">
				<PaletteIcon className="text-blue-500" size={20} />
			</div>
		),
		title: 'Branding',
		description: 'Customizing your AI Frontdesk Branding and Theme',
		url: 'https://www.myaifrontdesk.com/reseller-tutorials/customizing-your-ai-frontdesk-branding-and-theme',
	},
];

const ResellerBranding = () => {
	const [form] = Form.useForm();
	const dispatch = useAppDispatch();
	const currentReseller = useAppSelector(getResellerData);
	const [loading, setLoading] = useState<boolean>(false);
	const [previewImageUrl, setPreviewImageUrl] = useState<string>('');
	const [logoWidth, setLogoWidth] = useState<number>(64);
	const [logoHeight, setLogoHeight] = useState<number>(64);
	const [logoAdjustModalVisible, setLogoAdjustModalVisible] = useState<boolean>(false);
	const [isTutorialsModalOpen, setIsTutorialsModalOpen] = useState(false);

	const brandingTourRef1 = useRef<HTMLDivElement>(null);
	const brandingTourRef2 = useRef<HTMLDivElement>(null);
	const brandingTourRef3 = useRef<HTMLDivElement>(null);
	const brandingTourRef4 = useRef<HTMLDivElement>(null);

	const brandingTourSteps = [
		{
			title: <h1 className="font-bold text-2xl">Update your business name and logo</h1>,
			description: (
				<p className="text-base">
					Upload or change your logo and business name here. Need to resize your logo? Just click
					the <strong>Adjust</strong> button.
				</p>
			),
			target: brandingTourRef1.current as HTMLElement,
		},
		{
			title: (
				<h1 className="font-bold text-2xl">
					Purchase redirect link / Privacy Policy / Terms of Use
				</h1>
			),
			description: (
				<p className="text-base">
					If you're not using our Stripe rebilling, you can add your own payment link to redirect
					clients when upgrading from a free trial. You can also attach your privacy policy and
					terms of use.
				</p>
			),
			target: brandingTourRef2.current as HTMLElement,
		},
		{
			title: (
				<h1 className="font-bold text-2xl">Customize your clients dashboards with your branding</h1>
			),
			description: (
				<p className="text-base">
					Tailor your client's dashboard to match your branding. Just make sure you've configured a
					receptionist first under My Receptionists.
				</p>
			),
			target: brandingTourRef3.current as HTMLElement,
		},
		{
			title: (
				<h1 className="font-bold text-2xl">Add 2 custom buttons to your client's dashboard</h1>
			),
			description: (
				<p className="text-base">
					You can add up to two custom buttons to your clients' dashboards—perfect for linking to
					your support page, tutorials, or any other helpful resource.
				</p>
			),
			target: brandingTourRef4.current as HTMLElement,
		},
	];

	const queryClient = useQueryClient();

	const updateTutorialMutation = useMutation({
		mutationFn: updateTutorialStepStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	const updateToursMutation = useMutation({
		mutationFn: updateTourStatus,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['reseller'] });
		},
	});

	useEffect(() => {
		if (currentReseller) {
			form.setFieldsValue({
				resellerName: currentReseller?.businessName,
				themeColor: currentReseller?.theme_color,
				privacyPolicyURL: currentReseller?.privacyPolicyURL,
				termsOfUseURL: currentReseller?.termsOfUseURL,
				uploadedLogoS3Url: currentReseller?.logo,
				twilioAccountId: currentReseller?.inboundTwilioConfig?.accountId,
				twilioAccessToken: currentReseller?.inboundTwilioConfig?.accessToken,
				customButton1Label: currentReseller?.customButton1Label,
				customButton1URL: currentReseller?.customButton1URL,
				customButton2Label: currentReseller?.customButton2Label,
				customButton2URL: currentReseller?.customButton2URL,
				purchaseRedirectLink: currentReseller?.purchaseRedirectLink,
			});
			setLogoHeight(currentReseller?.logoDimensions?.height || 64);
			setLogoWidth(currentReseller?.logoDimensions?.width || 64);
		}
	}, [currentReseller, form]);

	const resellerTour = useAppSelector(getResellerTourState);

	useEffect(() => {
		if (!currentReseller?.emailAddress || resellerTour.open) {
			return;
		}

		if (!currentReseller?.tours?.branding) {
			updateToursMutation.mutate('branding');
			dispatch(
				setTour({
					open: true,
					currentStep: 0,
					returnTo: AppRoutes.RESELLER_BRANDING,
				}),
			);
		}
	}, [currentReseller?.emailAddress, resellerTour.open]);

	const mutation = useMutation({
		mutationKey: ['updateResellerBranding'],
		mutationFn: UpdateResellerBranding,
		onError(data) {
			notification.error({
				message: data.message,
			});
		},
		onSuccess(data) {
			if (data.success) {
				queryClient.invalidateQueries({ queryKey: ['reseller'] });
				notification.success({
					key: 'update-reseller',
					message: 'Updated successfully',
					duration: 3,
				});
			}
		},
	});

	const handleSubmit = async (values: {
		resellerName: string;
		themeColor: string;
		uploadedLogoS3Url: string;
		emailAddress: string;
		privacyPolicyURL: string;
		termsOfUseURL: string;
		purchaseRedirectLink: string;
		logoDimensions: { width: number; height: number };
		twilioAccessToken: string;
		twilioAccountId: string;
	}) => {
		const valuesWithEmail = {
			...values,
			emailAddress: currentReseller?.emailAddress as string,
			logoDimensions: { width: logoWidth, height: logoHeight },
		};
		await mutation.mutateAsync(valuesWithEmail);
		dispatch(
			updateReseller({
				logo: values.uploadedLogoS3Url,
				// logoUrl: previewImageUrl as string,
				logoDimensions: { width: logoWidth, height: logoHeight },
			}),
		);
	};
	const updateResellerBrandingToggleMutation = useMutation({
		mutationKey: ['UpdateResellerBrandingToggle'],
		mutationFn: (newBrandedValue: boolean) =>
			UpdateResellerBrandingToggle({
				userId: currentReseller?.id,
				branded: newBrandedValue,
			}),
		onSuccess: ({ success, branded }) => {
			if (success) {
				notification.success({
					key: 'update-reseller-branding',
					message: `Branding switched ${branded ? 'ON' : 'OFF'}`,
					duration: 3,
				});
			}
		},
	});

	const handleResellerBrandingToggle = (checked: boolean) => {
		dispatch(updateReseller({ branded: checked }));
		updateResellerBrandingToggleMutation.mutate(checked);
	};

	const handleChange: UploadProps['onChange'] = (info) => {
		if (info.file.status === 'uploading') {
			setLoading(true);
			return;
		}
		if (info.file.status === 'done') {
			form.setFieldValue('uploadedLogoS3Url', info.file.response.url);
			getBase64(info.file.originFileObj as FileType, (url) => {
				setLoading(false);
				setPreviewImageUrl(url);
			});
			setLoading(false);
		}
	};

	const uploadButton = (
		<button type="button">
			{loading ? <LoadingOutlined /> : <PlusOutlined />}
			<div className="mt-1 text-gray-500">Upload</div>
		</button>
	);

	const handleColorChange = (value: any) => {
		form.setFieldValue('themeColor', value.toHexString());
		dispatch(updateReseller({ theme_color: value.toHexString() }));
	};

	const LogoPreview = () => (
		<div className="bg-gray-50 rounded-lg p-4 mt-4">
			<h3 className="text-lg font-medium mb-4">Logo Preview</h3>
			{previewImageUrl && (
				<div className="flex justify-center items-center bg-white rounded p-4 mb-4 min-h-24">
					<img
						src={previewImageUrl}
						alt="logo preview"
						style={{
							width: `${logoWidth}px`,
							height: `${logoHeight}px`,
							objectFit: 'contain',
						}}
						className="max-w-full h-auto"
					/>
				</div>
			)}
			<div className="space-y-4">
				<div>
					<label className="block text-sm font-medium mb-2">Width: {logoWidth}px</label>
					<Slider
						min={32}
						max={200}
						value={logoWidth}
						onChange={(value) => setLogoWidth(value)}
						step={1}
						keyboard={true}
						tooltip={{
							formatter: (value) => `${value}px`,
						}}
					/>
				</div>
				<div>
					<label className="block text-sm font-medium mb-2">Height: {logoHeight}px</label>
					<Slider
						min={32}
						max={200}
						value={logoHeight}
						onChange={(value) => setLogoHeight(value)}
						step={1}
						keyboard={true}
						tooltip={{
							formatter: (value) => `${value}px`,
						}}
					/>
				</div>
			</div>
		</div>
	);

	const handleTutorialClick = () => {
		if (!currentReseller?.tutorial_steps?.branding_tutorial) {
			updateTutorialMutation.mutate('branding_tutorial');
		}
		setIsTutorialsModalOpen(true);
	};

	const handleTourClick = () => {
		setIsTutorialsModalOpen(false);

		if (!currentReseller?.tours?.branding) {
			updateToursMutation.mutate('branding');
		}
	};

	return (
		<ResellerDashboard activeKey={ResellerSiderItem.BRANDING}>
			<ThemeCustomizer />
			<CustomModal
				title="Adjust Logo"
				isModalOpen={logoAdjustModalVisible}
				cancelAction={() => {
					setLogoAdjustModalVisible(false);
					setLogoWidth(currentReseller?.logoDimensions?.width || 64);
					setLogoHeight(currentReseller?.logoDimensions?.height || 64);
				}}
				confirmAction={() => {
					setLogoAdjustModalVisible(false);
					handleSubmit(form.getFieldsValue());
				}}
			>
				<Typography.Text className="block mb-4">
					Adjust the width and height of your logo to fit your branding needs.
					<br />
					<strong>Press submit to save settings.</strong>
				</Typography.Text>
				<LogoPreview />
			</CustomModal>

			<div className="p-8">
				<div className="flex items-center justify-between mb-6">
					<div>
						<Title level={2} className="!mb-2">
							Branding
						</Title>
						<Text type="secondary">Customize your branding</Text>
					</div>

					<div className="flex items-center gap-2">
						<Button
							type="default"
							size="large"
							onClick={handleTutorialClick}
							className={
								!currentReseller?.tutorial_steps?.branding_tutorial
									? 'bg-[#F1F8FF] hover:bg-[#F1F8FF] text-blue-600 has-ping overflow-hidden'
									: ''
							}
						>
							<BookOpenIcon size={16} />
							<span>Tutorials</span>
						</Button>
					</div>
				</div>
				<Form form={form} onFinish={handleSubmit} layout="vertical" className="space-y-6">
					<Card className="mb-6 shadow border">
						<div ref={brandingTourRef1} className="flex flex-col md:flex-row">
							<div className="md:mr-7">
								<Upload
									listType="picture-circle"
									className="w-full"
									showUploadList={false}
									action={UPDATE_RESELLER_LOGO}
									onChange={handleChange}
									name="logo"
									accept="image/*"
								>
									<div className="w-full aspect-square max-w-48 mx-auto flex justify-center items-center">
										{previewImageUrl ? (
											<img
												style={{
													width: `${logoWidth}px`,
													height: `${logoHeight}px`,
													objectFit: 'contain',
												}}
												src={previewImageUrl}
												alt="logo"
												className="w-full h-full"
											/>
										) : (
											uploadButton
										)}
									</div>
								</Upload>
								{/* <LogoPreview /> */}
								<Button
									type="primary"
									onClick={() => {
										setLogoAdjustModalVisible(true);
									}}
									className="mt-4 ml-3"
								>
									Adjust
								</Button>
							</div>
							<div className="w-full md:w-2/3">
								<Form.Item label="Business name" name="resellerName">
									<Input placeholder="Please enter business name" />
								</Form.Item>
							</div>

							<Form.Item name="uploadedLogoS3Url" hidden />
						</div>
					</Card>

					<Card className="mb-6 shadow border">
						<div className="flex justify-between items-center">
							<div className="title-group">
								<Typography.Title level={4}>Theme Customization</Typography.Title>
								<Tag color="blue" className="beta-tag">
									BETA
								</Tag>
							</div>
							<Button
								type="primary"
								icon={<BgColorsOutlined />}
								size="large"
								onClick={() => dispatch(openModal({ modalId: MODAL_IDS.THEME_CUSTOMIZE_MODAL }))}
								className="customize-btn"
							>
								Customize Theme
							</Button>
						</div>
						<p className="mt-4 text-gray-500 text-base">
							Take control of your brand's visual identity across all platforms. Our theme
							customization tool lets you modify colors, styles and components.
						</p>
						<div className="flex flex-col md:flex-row gap-4">
							<Form.Item label="Theme color" name="themeColor" className="w-full">
								<ColorPicker
									className={'w-full border-none'}
									// get rid of square in the middle
									disabledAlpha={true}
									onChange={handleColorChange}
									style={{
										background: currentReseller?.theme_color,
										border: 'none',
										fontSize: 0,
										borderWidth: 0,
										display: 'block',
									}}
								/>
							</Form.Item>
						</div>
					</Card>

					<Card className="mb-6 shadow border">
						<p className="text-gray-700 text-lg font-semibold">Legal Information</p>

						<div ref={brandingTourRef2}>
							{/* URLs Section */}
							<div className="grid md:grid-cols-2 gap-4">
								<Form.Item label="Privacy Policy URL" name="privacyPolicyURL">
									<Input placeholder="please enter privacy policy url" />
								</Form.Item>
								<Form.Item label="Terms of use URL" name="termsOfUseURL">
									<Input placeholder="please enter terms of use url" />
								</Form.Item>
							</div>
						</div>

						<p className="text-gray-500">
							Add your own Privacy policy and Terms of use. If you don't have yours you can update
							ours from here: Privacy policy, Terms of Use.
						</p>
					</Card>

					<Card className="mb-6 shadow border">
						<p className="text-gray-700 text-lg font-semibold">
							Add Custom Buttons to Your Subaccount's Dashboards
						</p>
						<Typography.Paragraph className="text-gray-500">
							You can add custom buttons to your client's dashboard. Use these additional buttons to
							link directly to your support or tutorial pages, or allow clients to send you an email
							with a single click.
						</Typography.Paragraph>

						<div ref={brandingTourRef4}>
							<div className="grid md:grid-cols-2 gap-4">
								<Form.Item
									label={
										<div className="flex items-center">
											<span>Custom Button 1 Label</span>
											<Tooltip title="This button would be additionally shown in your client's dashboard on the sidebar you can put your desired label here">
												<InfoCircleOutlined className="ml-2 text-gray-400" />
											</Tooltip>
										</div>
									}
									name="customButton1Label"
								>
									<Input placeholder="Support" />
								</Form.Item>
								<Form.Item
									label={
										<div className="flex items-center">
											<span>Custom Button 1 URL</span>
											<Tooltip title="This is the redirect link for your button. Insert the URL where you want to take your customers">
												<InfoCircleOutlined className="ml-2 text-gray-400" />
											</Tooltip>
										</div>
									}
									name="customButton1URL"
								>
									<Input placeholder="www.resellersupportwebsite.com" />
								</Form.Item>
							</div>

							<div className="grid md:grid-cols-2 gap-4 mt-2">
								<Form.Item
									label={
										<div className="flex items-center">
											<span>Custom Button 2 Label</span>
											<Tooltip title="This button would be additionally shown in your client's dashboard on the sidebar you can put your desired label here">
												<InfoCircleOutlined className="ml-2 text-gray-400" />
											</Tooltip>
										</div>
									}
									name="customButton2Label"
								>
									<Input placeholder="Academy" />
								</Form.Item>
								<Form.Item
									label={
										<div className="flex items-center">
											<span>Custom Button 2 URL</span>
											<Tooltip title="This is the redirect link for your button. Insert the URL where you want to take your customers">
												<InfoCircleOutlined className="ml-2 text-gray-400" />
											</Tooltip>
										</div>
									}
									name="customButton2URL"
								>
									<Input placeholder="www.reselleracademywebsite.com" />
								</Form.Item>
							</div>
						</div>
					</Card>

					<Card className="mb-6 shadow border">
						<p className="text-gray-700 text-lg font-semibold">Advance Settings</p>

						<Form.Item
							label={
								<div className="flex items-center">
									<span>Purchase Redirect Link</span>
									<Tooltip title="If you haven't configured stripe billing, you can provide a custom purchase link here. Trial accounts will see a button to open this link. Once you receive payment, you can convert it to a real account manually.">
										<InfoCircleOutlined className="ml-2 text-gray-400" />
									</Tooltip>
								</div>
							}
							name="purchaseRedirectLink"
							rules={[
								{
									type: 'url',
									message: 'Please enter a valid URL',
								},
							]}
						>
							<Input placeholder="Enter the URL where customers will be redirected for purchase" />
						</Form.Item>
					</Card>

					{!currentReseller?.permanently_unbranded && (
						<Card className="mb-6 shadow border">
							<div className="flex items-center justify-between">
								<div>
									<div className="flex items-center">
										<span className="text-gray-700 text-lg font-semibold">
											White Label Dashboard Branding
										</span>
										<Tooltip title="When enabled, this will remove all AI Front Desk branding from your Admin Dashboard">
											<InfoCircleOutlined className="ml-2 text-gray-400" />
										</Tooltip>
									</div>
									<p className="text-gray-500">Remove our branding from your admin dashboard</p>
								</div>
								<Switch
									size="default"
									loading={updateResellerBrandingToggleMutation.isPending}
									onChange={handleResellerBrandingToggle}
									checked={currentReseller?.branded}
								/>
							</div>
						</Card>
					)}

					<Form.Item className="flex justify-end">
						<Button
							loading={loading || mutation.isPending}
							size="large"
							type="primary"
							htmlType="submit"
						>
							Submit
						</Button>
					</Form.Item>
				</Form>
			</div>

			<TutorialsModal
				isOpen={isTutorialsModalOpen}
				onClose={() => setIsTutorialsModalOpen(false)}
				tutorialItems={tutorialItems}
			>
				<TourButton onClick={handleTourClick} page="branding" />
			</TutorialsModal>
			<ResellerTour tourSteps={brandingTourSteps} />
		</ResellerDashboard>
	);
};

export default ResellerBranding;
